import { maybeParseNum, roundToString } from '@lib/calculations';
import { convertFromDisplayMagnitude } from '@lib/util/display-magnitude';
import { DisplayMagnitude } from '@prisma/client';
import { commaSeparated, currency } from '@ui/util';

type RawPriceBreak = {
  quantity: string | number;
  unitPrice: string | number;
};

type MinimalQuoteLine = RawPriceBreak & { quantityBreaks: RawPriceBreak[] };

type PriceBreak = {
  quantity: string;
  price: string;
  total: string;
};

export function formatPriceBreaks(
  lineItem: MinimalQuoteLine,
  quantityDisplayMagnitude: DisplayMagnitude,
  siteCode: string,
): PriceBreak[] {
  const mainLine = [
    { quantity: lineItem.quantity, unitPrice: lineItem.unitPrice },
  ] satisfies RawPriceBreak[];

  const quantityBreaks = lineItem.quantityBreaks.map(
    ({ quantity, unitPrice }) => ({
      quantity,
      unitPrice,
    }),
  ) satisfies RawPriceBreak[];

  const sortedPriceBreaks = [...mainLine, ...quantityBreaks].sort(
    (a, b) => Number(a.quantity) - Number(b.quantity),
  );

  return sortedPriceBreaks.map(({ quantity, unitPrice }) =>
    transformPriceBreak(
      quantity,
      unitPrice,
      quantityDisplayMagnitude,
      siteCode,
    ),
  );
}

function transformPriceBreak(
  quantity: string | number,
  unitPrice: string | number,
  quantityDisplayMagnitude: DisplayMagnitude,
  siteCode: string,
): PriceBreak {
  const quantityNumber = maybeParseNum(quantity, 0);
  const unitPriceNumber = maybeParseNum(unitPrice, 0);

  const formattedQuantity = commaSeparated(
    roundToString(
      convertFromDisplayMagnitude(quantityNumber, quantityDisplayMagnitude),
      { min: 0, small: 0 },
    ),
  );

  const formattedPrice = currency(
    unitPriceNumber,
    getCurrencyFormatForSite(siteCode),
  );

  const lineItemTotal = unitPriceNumber * maybeParseNum(quantity, 1);

  const formattedTotal = currency(
    lineItemTotal,
    getCurrencyFormatForSite(siteCode),
  );

  return {
    quantity: formattedQuantity,
    price: formattedPrice,
    total: formattedTotal,
  };
}

const DEFAULT_CURRENCY_FORMAT = {
  min: 2,
  small: 2,
  separator: true,
};

const SITE_CURRENCY_FORMATS: Record<string, typeof DEFAULT_CURRENCY_FORMAT> = {
  RAF: { ...DEFAULT_CURRENCY_FORMAT, min: 3, small: 4 },
  WWP: { ...DEFAULT_CURRENCY_FORMAT, min: 5, small: 5 },
} as const;

export function getCurrencyFormatForSite(siteCode: string) {
  const siteFormatConfig = SITE_CURRENCY_FORMATS[siteCode];
  if (!siteFormatConfig) {
    return DEFAULT_CURRENCY_FORMAT;
  }
  return siteFormatConfig;
}
